import { ArrowLeft } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  cancelSubscription,
  changeSubscriptionPlan,
  restoreSubscription,
} from '../api/subscriptions';
import AuthLayout from '../components/AuthLayout';
import AppButton from '../components/common/AppButton';
import AppModal from '../components/common/AppModal';
import SubscriptionBillingActions from '../components/subscription/SubscriptionBillingActions';
import type { SubscriptionPeriod } from '../components/subscription/SubscriptionCard';
import SubscriptionSelector from '../components/subscription/SubscriptionSelector';
import { plans } from '../config/subscriptionPlans';
import { useAuth } from '../context/AuthContext';
import type { PlanId } from '../types/subscription';

export default function SubscriptionManagePage() {
  const { company, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [period, setPeriod] = useState<SubscriptionPeriod>(
    company?.subscription?.billingPeriod || 'monthly'
  );
  const [plan, setPlan] = useState<PlanId | undefined>(
    company?.subscription?.tier || 'professional'
  );
  const [busy, setBusy] = useState(false);
  const operation = useRef<{
    plan: PlanId;
    period: SubscriptionPeriod;
    id: string;
  } | null>(null);
  const inFlight = useRef(false);
  const cancellationId = useRef<string | null>(null);
  const restorationId = useRef<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [planChange, setPlanChange] = useState<{
    companyId: string;
    plan: PlanId;
    period: SubscriptionPeriod;
  } | null>(null);
  const cancel = async () => {
    if (!company || !hasPermission('canManageSubscription') || inFlight.current)
      return;
    inFlight.current = true;
    setBusy(true);
    cancellationId.current ||= crypto.randomUUID();
    try {
      const result = await cancelSubscription(
        company.id,
        cancellationId.current
      );
      cancellationId.current = null;
      setConfirmCancel(false);
      toast.success(
        result.cancelAtPeriodEnd
          ? 'Your subscription will end at the end of the current billing period.'
          : 'Your subscription has been canceled.'
      );
    } catch (cause) {
      toast.error(
        cause instanceof Error
          ? cause.message
          : 'Unable to cancel your subscription. Please try again.'
      );
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  const restore = async () => {
    if (!company || !hasPermission('canManageSubscription') || inFlight.current)
      return;
    inFlight.current = true;
    setBusy(true);
    try {
      restorationId.current ||= crypto.randomUUID();
      await restoreSubscription(company.id, restorationId.current);
      restorationId.current = null;
      toast.success('Your subscription will continue renewing.');
    } catch (cause) {
      toast.error(
        cause instanceof Error
          ? cause.message
          : 'Unable to restore your subscription. Please try again.'
      );
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  const isOwner = hasPermission('canManageSubscription');
  const canManage = isOwner && company?.subscription?.store === 'stripe';
  const expirationDate = company?.subscription?.expirationDate;
  const endDate =
    expirationDate instanceof Date && Number.isFinite(expirationDate.getTime())
      ? new Intl.DateTimeFormat(undefined, {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          timeZone: company?.timezone,
        }).format(expirationDate)
      : undefined;
  const companyId = company?.id;
  const currentTier = company?.subscription?.tier;
  const currentPeriod = company?.subscription?.billingPeriod;
  useEffect(() => {
    operation.current = null;
    cancellationId.current = null;
    restorationId.current = null;
    setConfirmCancel(false);
    setPlanChange(null);
  }, [companyId]);
  useEffect(() => {
    setPeriod(currentPeriod || 'monthly');
    setPlan(currentTier || 'professional');
    setPlanChange(null);
  }, [companyId, currentTier, currentPeriod]);
  const change = async () => {
    if (
      !company ||
      !canManage ||
      !planChange ||
      planChange.companyId !== company.id ||
      inFlight.current
    )
      return;
    const { plan: next, period: nextPeriod } = planChange;
    inFlight.current = true;
    setBusy(true);
    setPlan(next);
    if (
      operation.current?.plan !== next ||
      operation.current.period !== nextPeriod
    )
      operation.current = {
        plan: next,
        period: nextPeriod,
        id: crypto.randomUUID(),
      };
    try {
      await changeSubscriptionPlan(
        company.id,
        next,
        nextPeriod,
        operation.current.id
      );
      operation.current = null;
      setPlanChange(null);
      toast.success(
        'Plan change submitted. Your subscription will update shortly.'
      );
    } catch (cause) {
      toast.error(
        cause instanceof Error
          ? cause.message
          : 'Unable to change your plan. Please try again.'
      );
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  return (
    <AuthLayout
      title="Manage your subscription"
      subtitle="Choose a new plan. Changing plans or billing periods may result in an immediate charge."
      multiColumn
      headerLeft={
        <AppButton
          variant="tertiary"
          disabled={busy}
          onClick={() => {
            if (location.key === 'default' || window.history.state?.idx === 0)
              navigate('/', { replace: true });
            else navigate(-1);
          }}
        >
          <span className="flex items-center gap-2">
            <ArrowLeft size={18} aria-hidden="true" />
            Back
          </span>
        </AppButton>
      }
    >
      <SubscriptionSelector
        plans={plans}
        period={period}
        selectedPlanId={
          plan ||
          (company?.subscription?.isActive && currentPeriod === period
            ? currentTier || undefined
            : undefined)
        }
        currentPlanId={
          company?.subscription?.isActive && currentPeriod === period
            ? currentTier || undefined
            : undefined
        }
        onSelectPlan={setPlan}
        onPeriodChange={(nextPeriod) => {
          if (nextPeriod !== period) {
            setPeriod(nextPeriod);
            setPlan(undefined);
          }
        }}
        onSubscribe={(next) => {
          if (company && canManage && !busy) {
            setPlanChange({ companyId: company.id, plan: next, period });
          }
        }}
        disabled={!canManage || busy || !!planChange}
        loading={busy}
        activeButtonLabel={busy ? 'Changing plan…' : undefined}
        buttonLabel="CHANGE PLAN"
      />
      {!isOwner && <p role="alert">Only the account owner can change plans.</p>}
      <SubscriptionBillingActions disabled={busy} managementPage />
      {canManage && company?.subscription?.isActive && (
        <div className="mt-6 flex flex-col gap-3">
          {company.subscription.autoRenew === false ? (
            <AppButton disabled={busy} onClick={() => void restore()}>
              Restore subscription
            </AppButton>
          ) : (
            <AppButton
              variant="tertiary"
              disabled={busy}
              onClick={() => setConfirmCancel(true)}
            >
              Cancel subscription
            </AppButton>
          )}
        </div>
      )}
      <AppModal
        open={!!planChange && !!canManage}
        onClose={() => {
          if (!busy) setPlanChange(null);
        }}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="plan-change-title"
          aria-describedby="plan-change-description"
        >
          <h2
            id="plan-change-title"
            className="mb-4 text-center text-xl font-extrabold text-dark"
          >
            Change subscription?
          </h2>
          <p
            id="plan-change-description"
            className="text-center text-sm font-medium text-light"
          >
            Switch to {plans.find((item) => item.id === planChange?.plan)?.name}{' '}
            with {planChange?.period === 'yearly' ? 'annual' : 'monthly'}{' '}
            billing. This change may result in an immediate charge.
          </p>
          <div className="mt-6 flex flex-col gap-3">
            <AppButton disabled={busy} onClick={() => void change()}>
              {busy ? 'Changing plan…' : 'Confirm plan change'}
            </AppButton>
            <AppButton
              variant="tertiary"
              disabled={busy}
              onClick={() => setPlanChange(null)}
            >
              Keep current plan
            </AppButton>
          </div>
        </div>
      </AppModal>
      <AppModal
        open={
          confirmCancel &&
          !!canManage &&
          !!company?.subscription?.isActive &&
          company.subscription.autoRenew !== false
        }
        onClose={() => {
          if (!busy) setConfirmCancel(false);
        }}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="cancel-subscription-title"
          aria-describedby="cancel-subscription-description"
        >
          <h2
            id="cancel-subscription-title"
            className="mb-4 text-center text-xl font-extrabold text-dark"
          >
            Cancel subscription?
          </h2>
          <p
            id="cancel-subscription-description"
            className="text-center text-sm font-medium text-light"
          >
            {endDate
              ? `Your subscription will end on ${endDate}. You can continue using your plan until then.`
              : 'Active subscriptions end at the end of the current billing period.'}
          </p>
          <div className="mt-6 flex flex-col gap-3">
            <AppButton disabled={busy} onClick={() => void cancel()}>
              {busy ? 'Canceling…' : 'Confirm cancellation'}
            </AppButton>
            <AppButton
              variant="tertiary"
              disabled={busy}
              onClick={() => setConfirmCancel(false)}
            >
              Keep subscription
            </AppButton>
          </div>
        </div>
      </AppModal>
    </AuthLayout>
  );
}
